// Which Claude models a curator may pick for a one-off enrichment run.
//
// A hardcoded list would go stale the day Anthropic ships anything (the same
// problem `listAnthropicModels` exists to solve). So this is a PREDICATE over
// the live list: the current frontier families, latest release of each. When
// Sonnet 6 appears in the API's list it appears in the picker, with no code
// change here.
//
// Deliberately narrower than the AI Prompts editor's dropdown, which still
// offers everything: that screen sets the standing model for a prompt type and
// occasionally needs an older id pinned. This is the per-run override, where
// the only sensible answers are today's models.

/** The frontier families, in the order a picker should list them. */
export const CURATED_MODEL_FAMILIES = ['opus', 'sonnet', 'haiku'] as const;
export type ModelFamily = (typeof CURATED_MODEL_FAMILIES)[number];

export interface ModelChoiceInput {
  id: string;
  displayName: string;
  createdAt?: string;
}

export interface ModelChoice {
  id: string;
  displayName: string;
  family: ModelFamily;
  /** Parsed version, e.g. 5 or 4.6 — the comparison key within a family. */
  version: number;
}

/** An 8-digit date suffix, e.g. the `20251001` of claude-haiku-4-5-20251001. */
const DATE_SUFFIX = /-(\d{8})$/;

/**
 * The family and version an id names, or null when it is not a current-gen
 * frontier model (claude-2, instant, or anything whose shape we don't know).
 *
 * Version digits are the groups between the family and an optional date:
 * `claude-opus-5` → 5, `claude-sonnet-4-6` → 4.6, `claude-haiku-4-5-20251001`
 * → 4.5. Read as major.minor so 5 sorts above 4.6 — a plain float parse of
 * "4-6" would not.
 */
export function parseModelId(id: string): { family: ModelFamily; version: number } | null {
  const family = CURATED_MODEL_FAMILIES.find((f) => id.includes(`-${f}-`) || id.endsWith(`-${f}`));
  if (!family) return null;
  const after = id.slice(id.indexOf(`-${family}`) + family.length + 1).replace(DATE_SUFFIX, '');
  const parts = after.split('-').filter((p) => /^\d+$/.test(p));
  if (parts.length === 0) return null;
  const [major, minor = '0'] = parts;
  return { family, version: Number(`${major}.${minor}`) };
}

/**
 * The models worth offering for a single run: the newest release of each
 * frontier family, newest family release first.
 *
 * Within one family and version, an UNDATED alias (`claude-haiku-4-5`) wins
 * over a dated pin (`claude-haiku-4-5-20251001`) — the alias keeps following
 * the family's latest build, which is what a curator picking "Haiku" means.
 */
export function curateModelChoices(models: readonly ModelChoiceInput[]): ModelChoice[] {
  const best = new Map<ModelFamily, ModelChoice & { dated: boolean; createdAt: string }>();

  for (const model of models) {
    const parsed = parseModelId(model.id);
    if (!parsed) continue;
    const candidate = {
      id: model.id,
      displayName: model.displayName || model.id,
      family: parsed.family,
      version: parsed.version,
      dated: DATE_SUFFIX.test(model.id),
      createdAt: model.createdAt ?? '',
    };
    const held = best.get(parsed.family);
    if (
      !held ||
      candidate.version > held.version ||
      // Same version: prefer the alias, then the more recent build.
      (candidate.version === held.version &&
        ((held.dated && !candidate.dated) ||
          (held.dated === candidate.dated && candidate.createdAt > held.createdAt)))
    ) {
      best.set(parsed.family, candidate);
    }
  }

  return CURATED_MODEL_FAMILIES.map((f) => best.get(f))
    .filter((m): m is ModelChoice & { dated: boolean; createdAt: string } => !!m)
    .map(({ id, displayName, family, version }) => ({ id, displayName, family, version }));
}
